import { getMobileOS } from "@/shared/utils/os_logic";
import React, { useEffect, useState } from "react";
import { OcrCameraAggregateModeType } from "@/shared/config/globals";

interface DetailViewOption {
  changeStatus: (mode: OcrCameraAggregateModeType) => void;
  imageList: string[];
  photoNumber: number;
}

export const DetailView = (props: DetailViewOption) => {

  // モーダルサイズ取得
  const getModalDimensions = () => {
    return {
      width: document.getElementsByClassName("md-photograph")[0].clientWidth,
      height: document.getElementsByClassName("md-photograph")[0].clientHeight,
    };
  };

  const getOrientation = () => {
    let orientation = "unknown";
    if (window.matchMedia("(orientation: portrait)").matches) {
      orientation = "portrait";
    }
    if (window.matchMedia("(orientation: landscape)").matches) {
      orientation = "landscape";
    }
    return orientation;
  };

  // カメラ領域の計算
  const getCameraSize = () => {
    const windowSize = getModalDimensions();
    const isPortrait = getOrientation() === "portrait";
    const os = getMobileOS();
    if (os === "iPhone" || os === "Android") {
      // Note: INVBG-152にてuserAgentを修正したが、カメラ領域に関してはコードを精査してから修正する必要があるため、今までと同様の状態になるようにiPadはこの分岐に含めていない
      if (isPortrait) {
        return {
          width: windowSize.width * (85 / 100.0),
          height: windowSize.height * (65 / 100.0),
        };
      } else {
        return {
          width: windowSize.width * (65 / 100.0),
          height: windowSize.height * (85 / 100.0),
        };
      }
    }
    return {
      width: windowSize.width * (85 / 100.0),
      height: windowSize.height * (65 / 100.0),
    };
  };


  // 現在のカメラ領域
  const [cameraSize, setCameraSize] = useState(getCameraSize());

  // 写真を表示するcanvasのid
  const photoCanvasId = "photo-shoot-modal-canvas";


  // キャプチャ画像の描画
  const renderImage = () => {
    const canvas = document.getElementById(photoCanvasId) as HTMLCanvasElement;
    // CSS で確定した表示サイズを描画バッファに反映（デフォルト 300×150 のまま描画されるのを防ぐ）
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const ctx = canvas.getContext("2d");
    const image = new Image();
    // 最大倍率
    const MAX_SCALE = 3;
    // 最小倍率
    const MIN_SCALE = 1;
    // 拡大倍率変化量
    const SCALE_STEP = 0.1;
    // 倍率のデフォルト値
    let imageScale = MIN_SCALE;
    let firstTouchX = 0;
    let firstTouchY = 0;
    // 拡大・縮小後の画像表示領域
    let zoomWidth = 0;
    let zoomHeight = 0;
    let zoomLeft = 0;
    let zoomTop = 0;
    let zoomLeftBuf = 0;
    let zoomTopBuf = 0;

    let touchStartArea = 0;
    let imageMove = false;

    // PC以外の場合
    if (getMobileOS() !== "Other") {
      // 画面タッチした場合
      const touchStart = (e: TouchEvent) => {
        // ２ヶ所タッチされていた場合
        if (e.touches.length > 1) {
          //絶対値を取得
          const touchStartX = Math.abs(e.touches[0].pageX - e.touches[1].pageX);
          const touchStartY = Math.abs(e.touches[0].pageY - e.touches[1].pageY);
          //はじめに2ヶ所タッチした時の面積
          touchStartArea = touchStartX * touchStartY;
          imageMove = false;
          // 1ヶ所のみタッチした場合
        } else if (e.touches.length === 1) {
          //はじめにタッチした箇所の位置
          const rect = (e.target as Element).getBoundingClientRect();
          firstTouchX = e.touches[0].pageX - rect.left;
          firstTouchY = e.touches[0].pageY - rect.top;
          zoomLeftBuf = zoomLeft;
          zoomTopBuf = zoomTop;
          imageMove = true;
        }
      };

      // 拡大・縮小処理
      const resize = (e: TouchEvent, touchX: number, touchY: number) => {
        // タッチした2ヶ所の面積を計算
        const touchMoveX = Math.abs(e.touches[1].pageX - e.touches[0].pageX);
        const touchMoveY = Math.abs(e.touches[1].pageY - e.touches[0].pageY);
        const touchMoveArea = touchMoveX * touchMoveY;

        // ピンチアウトした場合
        if (touchStartArea + 500 < touchMoveArea) {
          touchStartArea = touchMoveArea;
          imageExpansion(touchX, touchY);
        }
        // ピンチインした場合
        else if (touchStartArea - 500 > touchMoveArea) {
          touchStartArea = touchMoveArea;
          imageReduction(touchX, touchY);
        }
      };

      // 画像の拡大
      const imageExpansion = (touchX: number, touchY: number) => {
        imageScale += SCALE_STEP;
        // 現在の拡大倍率が最大値以上の場合は拡大はしない
        if (imageScale > MAX_SCALE) {
          imageScale = MAX_SCALE;
        } else {
          zoomWidth = canvas.width / imageScale;
          zoomHeight = canvas.height / imageScale;
          // 画像の表示開始座標を設定
          zoomLeft += (touchX * SCALE_STEP) / (imageScale * (imageScale - SCALE_STEP));
          zoomLeft = Math.max(0, Math.min(canvas.width - zoomWidth, zoomLeft));
          zoomTop += (touchY * SCALE_STEP) / (imageScale * (imageScale - SCALE_STEP));
          zoomTop = Math.max(0, Math.min(canvas.height - zoomHeight, zoomTop));
        }
      };

      // 画像の縮小
      const imageReduction = (touchX: number, touchY: number) => {
        imageScale -= SCALE_STEP;
        if (imageScale < 1) {
          imageScale = 1;
          zoomLeft = 0;
          zoomTop = 0;
        } else {
          zoomWidth = canvas.width / imageScale;
          zoomHeight = canvas.height / imageScale;
          // 画像の表示開始座標を設定
          zoomLeft -= (touchX * SCALE_STEP) / (imageScale * (imageScale + SCALE_STEP));
          zoomLeft = Math.max(0, Math.min(canvas.width - zoomWidth, zoomLeft));
          zoomTop -= (touchY * SCALE_STEP) / (imageScale * (imageScale + SCALE_STEP));
          zoomTop = Math.max(0, Math.min(canvas.height - zoomHeight, zoomTop));
        }
      };

      // ズーム
      const imageZoom = (e: TouchEvent) => {
        // 2ヶ所タッチしている場合
        if (e.touches.length > 1) {
          // Canvas上のタッチしている座標を取得
          const rect = (e.target as Element).getBoundingClientRect();
          // タッチ座標
          const touchX = Math.abs(e.touches[0].pageX + e.touches[1].pageX) / 2 - rect.left;
          const touchY = Math.abs(e.touches[0].pageY + e.touches[1].pageY) / 2 - rect.top;

          // 画像サイズの変更
          resize(e, touchX, touchY);

          draw();
        }
        // 画像の表示箇所の移動
        else if (e.touches.length === 1 && imageMove && imageScale > 1) {
          const rect = (e.target as Element).getBoundingClientRect();
          // ドラッグ処理
          const touchDragX = e.touches[0].pageX - rect.left;
          const touchDragY = e.touches[0].pageY - rect.top;

          zoomLeft = zoomLeftBuf + (firstTouchX - touchDragX) / imageScale;
          zoomLeft = Math.max(0, Math.min(canvas.width - zoomWidth, zoomLeft));

          zoomTop = zoomTopBuf + (firstTouchY - touchDragY) / imageScale;
          zoomTop = Math.max(0, Math.min(canvas.height - zoomHeight, zoomTop));
          draw();
        }
      };
      // Canvas上ではブラウザのスクロールを無効に
      const disableScroll = () => {
        document.addEventListener("touchmove", scrollControl, {
          passive: false,
        });
        canvas.addEventListener("touchmove", imageZoom);
      };
      const enableScroll = () => {
        document.removeEventListener("touchmove", scrollControl);
        canvas.removeEventListener("touchmove", imageZoom);
      };
      const scrollControl = (e: TouchEvent) => {
        e.preventDefault();
      };

      canvas.addEventListener("touchstart", touchStart);
      canvas.addEventListener("touchstart", disableScroll);
      canvas.addEventListener("touchend", enableScroll);
    }
    const draw = () => {
      // 毎回クリアしてから描画（ゴースト防止）
      ctx?.clearRect(0, 0, canvas.width, canvas.height);

      if (!image.complete || !image.naturalWidth) return;

      // 画像のアスペクト比
      const canvasAspect = canvas.width / canvas.height;
      const imgAspect = image.width / image.height;

      // object-fit: cover: canvas全面を埋めるようにクロップして描画
      let srcX: number, srcY: number, srcW: number, srcH: number;
      if (imgAspect >= canvasAspect) {
        // 横長画像 → 高さに合わせて左右クロップ
        srcH = image.height / imageScale;
        srcW = srcH * canvasAspect;
        srcX = (image.width / imageScale - srcW) / 2 + zoomLeft;
        srcY = zoomTop;
      } else {
        // 縦長画像 → 幅に合わせて上下クロップ
        srcW = image.width / imageScale;
        srcH = srcW / canvasAspect;
        srcX = zoomLeft;
        srcY = (image.height / imageScale - srcH) / 2 + zoomTop;
      }

      // ソース座標を画像範囲内にクランプ（範囲外参照による余白発生を防止）
      srcX = Math.max(0, Math.min(image.width - srcW, srcX));
      srcY = Math.max(0, Math.min(image.height - srcH, srcY));

      ctx?.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, canvas.width, canvas.height);
    };

    // 画像ロード完了後に描画（onload で上書きして多重登録を防ぐ）
    image.onload = draw;
    image.src = props.imageList[props.photoNumber];
    draw(); // ロード済みの場合は即描画
  };

  // 画面リサイズ時の処理
  const handleWindowResize = () => {
    setCameraSize(getCameraSize());
  };

  // 描画完了時
  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    // 画像の描画
    renderImage();
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  });

  return (
    <>
      <div className="confirm-image">
        {/* キャプチャ画像 */}
          <canvas id={photoCanvasId}></canvas>
      </div>
    </>
  );
};
